'use client';
import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FormEvent,
  type ReactNode,
} from 'react';
import { useLocation, useRevalidator } from 'react-router';
import { I18nextProvider, initReactI18next, useTranslation } from 'react-i18next';
import i18next, { type i18n as I18nInstance } from 'i18next';
import {
  cancelOrder,
  createOrder,
  fetchAppConfig,
  fetchCategories,
  fetchManufacturers,
  fetchProducts,
  fetchUserOrders,
  submitPaymentCheck,
} from '../api';
import { fetchWebSession, loginWithEmail, logoutWeb, registerWithEmail } from '../auth';
import type { WebsiteAppContextValue } from '../appContext';
import type { CartItemSummary, OrderResult, PaymentMethod } from '../appTypes';
import type {
  AppConfig,
  Category,
  Manufacturer,
  OrderLocation,
  Product,
  WebSessionUser,
} from '../types';
import { sortCategoriesByWeight } from '../../miniapp/utils/categorySorting';
import { getLocalizedApiErrorMessage } from '../utils/errorHelpers';
import { getEnabledWebsitePaymentModules } from '../payments/modules';
import { cartKey, getWeightPrice, parseCartKey } from '../../miniapp/utils/weight';
import { useCartStore, writeCart } from './cartStore';
import { useTransitionNavigate } from './TransitionLink';
import { FALLBACK_LANGUAGE, SUPPORTED_LANGUAGES, websiteResources } from '../i18n/resources';
import legacyI18n from '../i18n';
import type { WebsiteLanguage } from '../../utils/websiteSsr/contract';

export const WebsiteStoreContext = createContext<WebsiteAppContextValue | null>(null);
type Props = {
  initialSessionUser: WebSessionUser | null;
  initialAppConfig: AppConfig | null;
  ssr: boolean;
  language?: WebsiteLanguage;
  children: ReactNode;
};
const paymentMethods = (config: AppConfig | null): PaymentMethod[] =>
  getEnabledWebsitePaymentModules(config).map(({ code }) => code);
const languageInstances = new Map<WebsiteLanguage, I18nInstance>();
function getLanguageInstance(language: WebsiteLanguage): I18nInstance {
  const cached = languageInstances.get(language);
  if (cached) return cached;
  const instance = i18next.createInstance();
  void instance
    .use(initReactI18next)
    .init({
      resources: websiteResources,
      lng: language,
      fallbackLng: FALLBACK_LANGUAGE,
      supportedLngs: [...SUPPORTED_LANGUAGES],
      load: 'languageOnly',
      interpolation: { escapeValue: false },
      initAsync: false,
    });
  languageInstances.set(language, instance);
  return instance;
}

function StoreStateProvider({ initialSessionUser, initialAppConfig, ssr, children }: Props) {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useTransitionNavigate();
  const revalidator = useRevalidator();
  const cart = useCartStore();
  const [sessionLoading, setSessionLoading] = useState(!ssr);
  const [sessionUser, setSessionUser] = useState<WebSessionUser | null>(initialSessionUser);
  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [manufacturers, setManufacturers] = useState<Manufacturer[]>([]);
  const [orders, setOrders] = useState<WebsiteAppContextValue['orders']>([]);
  const [appConfig, setAppConfig] = useState<AppConfig | null>(initialAppConfig);
  const [catalogLoading, setCatalogLoading] = useState(true);
  const [catalogLoaded, setCatalogLoaded] = useState(false);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [error, setError] = useState('');
  const [deliveryAddress, updateDeliveryAddress] = useState('');
  const [deliveryLocation, updateDeliveryLocation] = useState<OrderLocation | null>(null);
  const [phoneNumber, updatePhoneNumber] = useState('');
  const [saveAddress, setSaveAddress] = useState(true);
  const [paymentMethod, updatePaymentMethod] = useState<PaymentMethod>('IBAN');
  const [checkoutPending, setCheckoutPending] = useState(false);
  const [orderResult, setOrderResult] = useState<OrderResult | null>(null);
  const [uploadingOrderId, setUploadingOrderId] = useState<number | null>(null);
  const [updatingOrderId, setUpdatingOrderId] = useState<number | null>(null);
  const [loginEmail, updateLoginEmail] = useState('');
  const [loginPassword, updateLoginPassword] = useState('');
  const [authMode, setAuthMode] = useState<WebsiteAppContextValue['authMode']>('login');
  const [authNotice, setAuthNotice] = useState('');
  const [authPending, setAuthPending] = useState(false);
  const catalogPromise = useRef<Promise<void> | null>(null);

  const resolveErrorMessage = useCallback(
    (err: unknown) => getLocalizedApiErrorMessage(err, t, { exists: i18n.exists.bind(i18n) }),
    [i18n, t],
  );
  const refreshSessionUser = useCallback(async () => {
    try {
      const response = await fetchWebSession();
      setSessionUser(response.authenticated ? response.data : null);
    } catch {
      setSessionUser(null);
    }
  }, []);
  const ensureCatalogLoaded = useCallback((): Promise<void> => {
    if (catalogLoaded) return Promise.resolve();
    if (catalogPromise.current) return catalogPromise.current;
    const request = Promise.all([fetchProducts(), fetchCategories(), fetchManufacturers()])
      .then(([p, c, m]) => {
        setProducts(p.filter((product) => product.enabled));
        setCategories(sortCategoriesByWeight(c));
        setManufacturers(m);
        setCatalogLoaded(true);
      })
      .catch((err: unknown) => {
        setError(resolveErrorMessage(err));
      })
      .finally(() => {
        setCatalogLoading(false);
        catalogPromise.current = null;
      });
    catalogPromise.current = request;
    return request;
  }, [catalogLoaded, resolveErrorMessage]);

  useEffect(() => {
    if (!ssr) void refreshSessionUser().finally(() => setSessionLoading(false));
  }, [refreshSessionUser, ssr]);
  useEffect(() => {
    if (appConfig === null)
      void fetchAppConfig()
        .then(setAppConfig)
        .catch((err: unknown) => setError(resolveErrorMessage(err)));
  }, [appConfig, resolveErrorMessage]);
  useEffect(() => {
    if (Object.keys(cart).length > 0 && !catalogLoaded) void ensureCatalogLoaded();
  }, [cart, catalogLoaded, ensureCatalogLoaded]);
  useEffect(() => {
    setError('');
  }, [location.pathname]);

  const enabledPaymentMethods = useMemo(() => paymentMethods(appConfig), [appConfig]);
  useEffect(() => {
    if (!enabledPaymentMethods.includes(paymentMethod))
      updatePaymentMethod(enabledPaymentMethods[0] ?? 'CASH_ON_DELIVERY');
  }, [enabledPaymentMethods, paymentMethod]);
  const cartItems = useMemo<CartItemSummary[]>(() => {
    const items: CartItemSummary[] = [];
    for (const [key, quantity] of Object.entries(cart)) {
      const parsed = parseCartKey(key);
      if (!parsed || quantity <= 0) continue;
      const product = products.find((entry) => entry.id === parsed.productId);
      if (!product) continue;
      const unitPrice = getWeightPrice(product, parsed.weight);
      if (unitPrice <= 0) continue;
      items.push({
        product,
        quantity,
        weight: parsed.weight,
        subtotal: Number((unitPrice * quantity).toFixed(2)),
      });
    }
    return items;
  }, [cart, products]);
  const cartCount = catalogLoaded
    ? cartItems.reduce((sum, item) => sum + item.quantity, 0)
    : Object.values(cart).reduce((sum, quantity) => sum + quantity, 0);
  const cartTotal = Number(cartItems.reduce((sum, item) => sum + item.subtotal, 0).toFixed(2));
  const setCartQuantity = (id: number, weight: number, delta: number) => {
    const key = cartKey(id, weight);
    const next = { ...cart };
    const quantity = (next[key] ?? 0) + delta;
    if (quantity > 0) next[key] = quantity;
    else delete next[key];
    writeCart(next);
  };
  const refreshUserOrders = useCallback(async () => {
    if (!sessionUser) {
      setOrders([]);
      return;
    }
    setOrdersLoading(true);
    try {
      setOrders(await fetchUserOrders());
    } catch (err) {
      setError(resolveErrorMessage(err));
    } finally {
      setOrdersLoading(false);
    }
  }, [resolveErrorMessage, sessionUser]);
  useEffect(() => {
    if (location.pathname === '/orders' && sessionUser) void refreshUserOrders();
  }, [location.pathname, refreshUserOrders, sessionUser]);

  async function submitAuthForm(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError('');
    setAuthNotice('');
    setAuthPending(true);
    try {
      if (authMode === 'register') {
        const registration = await registerWithEmail(loginEmail, loginPassword);
        updateLoginPassword('');
        setAuthMode('login');
        setAuthNotice(
          registration.confirmationUrl
            ? t('website.auth.confirmationSentWithLink', {
                email: loginEmail,
                url: registration.confirmationUrl,
              })
            : t('website.auth.confirmationSent', { email: loginEmail }),
        );
        return;
      }
      const response = await loginWithEmail(loginEmail, loginPassword);
      setSessionUser(response.data);
      updateLoginPassword('');
      navigate('/');
      if (ssr) revalidator.revalidate();
    } catch (err) {
      setError(resolveErrorMessage(err));
    } finally {
      setAuthPending(false);
    }
  }
  async function logout() {
    try {
      await logoutWeb();
      setSessionUser(null);
      navigate('/');
      if (ssr) revalidator.revalidate();
    } catch (err) {
      setError(resolveErrorMessage(err));
    }
  }
  async function submitCheckout(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError('');
    if (!sessionUser) {
      navigate('/login');
      return;
    }
    if (!cartItems.length) {
      setError(t('order.errors.cartEmpty'));
      return;
    }
    const address = deliveryAddress.trim(),
      phone = phoneNumber.trim();
    if (!address) {
      setError(t('order.errors.deliveryAddressRequired'));
      return;
    }
    if (!phone) {
      setError(t('order.errors.phoneRequired'));
      return;
    }
    if (!deliveryLocation) {
      setError(t('order.errors.locationRequired'));
      return;
    }
    setCheckoutPending(true);
    try {
      const response = await createOrder({
        deliveryAddress: address,
        phoneNumber: phone,
        paymentMethod,
        languageCode: i18n.resolvedLanguage ?? i18n.language,
        items: cartItems.map(({ product, quantity, weight }) => ({
          productId: product.id,
          quantity,
          weight,
        })),
        location: deliveryLocation,
        saveAddress,
      });
      setOrderResult({
        id: response.id,
        orderNumber: response.orderNumber,
        paymentMethod: response.paymentMethod,
        paymentInstruction: response.paymentInstruction,
        total: response.total,
      });
      writeCart({});
      updateDeliveryAddress('');
      updateDeliveryLocation(null);
      updatePhoneNumber('');
      setSaveAddress(true);
      updatePaymentMethod(enabledPaymentMethods[0] ?? 'CASH_ON_DELIVERY');
      navigate('/order-success');
      await refreshUserOrders();
    } catch (err) {
      setError(resolveErrorMessage(err));
    } finally {
      setCheckoutPending(false);
    }
  }
  async function cancelUserOrder(id: number) {
    setUpdatingOrderId(id);
    setError('');
    try {
      const updated = await cancelOrder(id);
      setOrders((current) => current.map((item) => (item.id === updated.id ? updated : item)));
    } catch (err) {
      setError(resolveErrorMessage(err));
    } finally {
      setUpdatingOrderId(null);
    }
  }
  async function uploadOrderPaymentCheck(id: number, files: FileList | null) {
    const file = files?.[0];
    if (!file) return;
    setUploadingOrderId(id);
    setError('');
    try {
      const updated = await submitPaymentCheck(id, [file]);
      setOrders((current) => current.map((item) => (item.id === updated.id ? updated : item)));
    } catch (err) {
      setError(resolveErrorMessage(err));
    } finally {
      setUploadingOrderId(null);
    }
  }
  const value: WebsiteAppContextValue = {
    ssr,
    ensureCatalogLoaded,
    sessionLoading,
    sessionUser,
    products,
    categories,
    manufacturers,
    orders,
    appConfig,
    catalogLoading,
    ordersLoading,
    error,
    cartItems,
    cartCount,
    cartTotal,
    orderResult,
    deliveryAddress,
    deliveryLocation,
    phoneNumber,
    saveAddress,
    paymentMethod,
    checkoutPending,
    uploadingOrderId,
    updatingOrderId,
    loginEmail,
    loginPassword,
    authMode,
    authPending,
    authNotice,
    setError,
    clearError: () => setError(''),
    clearOrderResult: () => setOrderResult(null),
    addToCart: (id, weight) => setCartQuantity(id, weight, 1),
    removeFromCart: (id, weight) => setCartQuantity(id, weight, -1),
    setDeliveryAddress: updateDeliveryAddress,
    setDeliveryLocation: updateDeliveryLocation,
    setPhoneNumber: updatePhoneNumber,
    setSaveAddress,
    setPaymentMethod: updatePaymentMethod,
    setLoginEmail: updateLoginEmail,
    setLoginPassword: updateLoginPassword,
    toggleAuthMode: () => {
      setAuthNotice('');
      setAuthMode((current) => (current === 'login' ? 'register' : 'login'));
    },
    setSessionUser,
    refreshSessionUser,
    submitAuthForm,
    logout,
    submitCheckout,
    refreshUserOrders,
    cancelUserOrder,
    uploadOrderPaymentCheck,
  };
  return <WebsiteStoreContext value={value}>{children}</WebsiteStoreContext>;
}

export function StoreProvider(props: Props) {
  const languageI18n = props.ssr
    ? getLanguageInstance(props.language ?? FALLBACK_LANGUAGE)
    : legacyI18n;
  return (
    <I18nextProvider i18n={languageI18n}>
      <StoreStateProvider {...props} />
    </I18nextProvider>
  );
}
export default StoreProvider;
